import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { createRecipe, duplicateRecipe, parseRecipeFile, portableCopy, recipeFileName, renameRecipe, retargetRecipe, serializeRecipes, suggestBinding, type MotionRecipe } from '../../animation/procedural/recipes';
import { CHARACTER_TEMPLATES, OBJECT_TEMPLATES, TEMPLATES, describeStep, resolvedParams, type MotionPath, type MotionStep, type MotionStepInput, type ParamDef } from '../../animation/procedural/spec';
import { parsePlan } from '../../animation/procedural/compiler';
import { CONFLICT_POLICIES, describeConflict, applyMotionPlan, type ConflictPolicy } from '../../domain/motionOps';
import { EasingSchema, type Animation, type Project, type Scene } from '../../domain/schemas';
import { downloadText } from '../../lib/download';
import { useObservable } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Alert, Button, Panel } from '../ui';
import type { MotionPreview } from './useMotionPreview';
import type { EditFn } from './useSceneEdit';

const sel = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const num = 'w-24 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm';
const label = 'text-[11px] text-zinc-400';

/**
 * Motion Composer (Phase 3C): build a plan of procedural motion steps, preview it on the
 * canvas and timeline (nothing is committed), then apply it as ordinary keyframes in one
 * undo step under an explicit conflict policy. Plans can be saved as reusable recipes.
 * This is deterministic procedural generation from templates — not AI.
 */
export function MotionComposer({ project, scene, active, edit, preview }: { project: Project; scene: Scene; active: Animation | null; edit: EditFn; preview: MotionPreview }) {
  const { motion, selection, recipes: repo } = useServices();
  const state = useObservable(motion);
  const selected = useObservable(selection);
  const [message, setMessage] = useState<{ tone: 'info' | 'warning' | 'error'; text: string } | null>(null);
  const step = state.steps.find((s) => s.id === state.selectedStepId) ?? null;
  const parsedStep = parseStepSafely(step);
  const selectedObjects = scene.objects.filter((o) => selected.objectIds.includes(o.id));
  const slotLabel = (slot: string) => {
    const id = state.binding[slot];
    return scene.objects.find((o) => o.id === id)?.name ?? state.slots.find((s) => s.name === slot)?.label ?? slot;
  };

  useEffect(() => motion.relabel(scene), [motion, scene]);

  const add = (template: MotionStepInput['template']) => {
    const problem = motion.addStep(template, selectedObjects);
    setMessage(problem ? { tone: 'error', text: problem } : null);
  };

  const apply = () => {
    const request = preview.request;
    if (!request) return;
    let created: string | null = null;
    let summary = '';
    const ok = edit('Apply motion plan', (p) => {
      const r = applyMotionPlan(p, request);
      created = r.createdAnimation ? r.animationId : null;
      summary = r.summary;
      return r.project;
    });
    if (ok) {
      if (created) selection.selectAnimation(created);
      motion.update({ preview: false });
      setMessage({ tone: 'info', text: `Applied. ${summary} Undo reverts it in one step.` });
    }
  };

  return (
    <Panel
      title="Motion Composer"
      headingLevel={2}
      actions={
        <Button size="sm" onClick={() => motion.setOpen(!state.open)} aria-expanded={state.open} data-testid="motion-toggle">
          {state.open ? 'Hide' : 'Open'}
        </Button>
      }
    >
      <div data-testid="motion-composer" className="flex flex-col gap-3">
        <p className="text-xs text-zinc-400">
          Procedural motion from a fixed template library (deterministic — not AI). Plans are previewed without changing anything; applying adds ordinary, editable keyframes.
        </p>
        {state.open && (
          <>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-3">
                <Library onAdd={add} hasCharacter={selectedObjects.some((o) => o.type === 'character') || state.slots.some((s) => s.kind === 'character')} />
                <Targets scene={scene} />
                <StepList slotLabel={slotLabel} />
              </div>
              <div className="flex flex-col gap-3">
                {step && parsedStep ? <StepInspector step={step} parsed={parsedStep} scene={scene} slotLabel={slotLabel} onMessage={setMessage} /> : <p className="text-xs text-zinc-400">Add a motion from the library, then select a step to edit its parameters.</p>}
                <PlanOptions />
              </div>
            </div>
            <ApplyBar active={active} preview={preview} onApply={apply} />
            <Recipes project={project} scene={scene} onMessage={setMessage} repo={repo} />
          </>
        )}
        {message && (
          <Alert tone={message.tone === 'error' ? undefined : message.tone} title={message.text} action={<Button size="sm" variant="ghost" onClick={() => setMessage(null)}>Dismiss</Button>} />
        )}
      </div>
    </Panel>
  );
}

/** The selected step with defaults filled in (as typed when a value is invalid). */
function parseStepSafely(step: MotionStepInput | null): MotionStep | null {
  if (!step) return null;
  try {
    return parsePlan({ version: 1, slots: step.targets.map((t) => ({ name: t, kind: 'character', label: t })), steps: [{ ...step, timing: { ...step.timing, ref: null, mode: step.timing?.mode === 'at' ? 'at' : 'sequence' } }] }).steps[0]!;
  } catch {
    // An invalid parameter: show the step as typed (the preview reports the problem).
    return { duration: null, repeat: 1, easing: null, params: {}, path: null, variation: 0, seed: null, stagger: 0, ...step, timing: { mode: 'sequence', ref: null, at: null, delay: 0, ...step.timing } } as MotionStep;
  }
}

function Library({ onAdd, hasCharacter }: { onAdd: (t: MotionStepInput['template']) => void; hasCharacter: boolean }) {
  return (
    <div className="flex flex-col gap-2" role="group" aria-label="Motion library">
      <p className="text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Library · adds a step for the canvas selection</p>
      {(
        [
          ['Character', CHARACTER_TEMPLATES],
          ['Objects', OBJECT_TEMPLATES],
        ] as const
      ).map(([group, ids]) => (
        <div key={group} className="flex flex-wrap items-center gap-1">
          <span className="w-20 text-[11px] text-zinc-400">{group}</span>
          {ids.map((id) => (
            <Button key={id} size="sm" title={TEMPLATES[id].description} onClick={() => onAdd(id)} disabled={TEMPLATES[id].characterOnly && !hasCharacter} data-testid={`motion-add-${id}`}>
              {TEMPLATES[id].label}
            </Button>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Slots (targets) and the objects they are bound to; rebinding retargets the plan. */
function Targets({ scene }: { scene: Scene }) {
  const { motion } = useServices();
  const state = useObservable(motion);
  if (!state.slots.length) return null;
  return (
    <div className="flex flex-col gap-1" role="group" aria-label="Targets" data-testid="motion-targets">
      <p className="text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Targets{state.recipeName ? ` · recipe “${state.recipeName}”` : ''}</p>
      {state.slots.map((slot) => {
        const options = scene.objects.filter((o) => slot.kind === 'object' || o.type === 'character');
        return (
          <div key={slot.name} className="flex items-center gap-2 text-xs">
            <label htmlFor={`slot-${slot.name}`} className="w-28 truncate text-zinc-300" title={slot.name}>
              {slot.label} <span className="text-zinc-400">({slot.kind})</span>
            </label>
            <select id={`slot-${slot.name}`} className={sel} value={state.binding[slot.name] ?? ''} onChange={(e) => motion.bind(slot.name, e.target.value)} data-testid="motion-slot">
              <option value="" disabled>
                Choose…
              </option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}

function StepList({ slotLabel }: { slotLabel: (slot: string) => string }) {
  const { motion } = useServices();
  const state = useObservable(motion);
  if (!state.steps.length) return <p className="text-xs text-zinc-400">No steps yet.</p>;
  return (
    <ol className="flex flex-col gap-1" aria-label="Motion steps" data-testid="motion-steps">
      {state.steps.map((s, i) => {
        let text = TEMPLATES[s.template].label;
        try {
          text = describeStep(parsePlan({ version: 1, slots: s.targets.map((t) => ({ name: t, kind: 'character', label: t })), steps: [{ ...s, timing: { ...s.timing, ref: null, mode: s.timing?.mode === 'at' ? 'at' : 'sequence' } }] }).steps[0]!, slotLabel);
          text = text.replace(/ · then$/, s.timing?.mode === 'with' ? ' · with previous' : s.timing?.mode === 'after' ? ' · after a step' : ' · then');
        } catch {
          // Invalid values: the plain label is shown; the preview explains the problem.
        }
        const on = s.id === state.selectedStepId;
        return (
          <li key={s.id} className={`flex items-center gap-1 rounded-md border px-1 py-0.5 ${on ? 'border-sky-500 bg-sky-950/40' : 'border-zinc-800 bg-zinc-900/60'}`} data-testid="motion-step">
            <button type="button" className="flex-1 truncate px-1 text-left text-xs text-zinc-200" onClick={() => motion.update({ selectedStepId: s.id })} aria-pressed={on} data-testid="motion-step-select">
              {i + 1}. {text}
            </button>
            <Button size="sm" variant="ghost" onClick={() => motion.moveStep(s.id, -1)} disabled={i === 0} aria-label={`Move step ${i + 1} up`}>
              ↑
            </Button>
            <Button size="sm" variant="ghost" onClick={() => motion.moveStep(s.id, 1)} disabled={i === state.steps.length - 1} aria-label={`Move step ${i + 1} down`}>
              ↓
            </Button>
            <Button size="sm" variant="ghost" onClick={() => motion.duplicateStep(s.id)} aria-label={`Duplicate step ${i + 1}`}>
              ⧉
            </Button>
            <Button size="sm" variant="ghost" onClick={() => motion.removeStep(s.id)} aria-label={`Delete step ${i + 1}`}>
              ×
            </Button>
          </li>
        );
      })}
    </ol>
  );
}

interface NumberFieldProps {
  id: string;
  text: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  testId?: string;
}

/** A number box committed on blur/Enter; empty means "auto". Remounts when the value changes elsewhere. */
function NumberField(props: NumberFieldProps) {
  return <NumberFieldInput key={String(props.value ?? '')} {...props} />;
}

function NumberFieldInput({ id, text, value, onChange, step = 0.1, min, max, placeholder, testId }: NumberFieldProps) {
  const [draft, setDraft] = useState(value === null || value === undefined ? '' : String(value));
  const commit = () => {
    const t = draft.trim();
    if (t === '') return onChange(null);
    const v = Number(t);
    if (Number.isFinite(v)) onChange(v);
    else setDraft(value === null || value === undefined ? '' : String(value));
  };
  return (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className={label}>
        {text}
      </label>
      <input
        id={id}
        type="number"
        className={num}
        value={draft}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
        data-testid={testId}
      />
    </div>
  );
}

function StepInspector({ step, parsed, scene, slotLabel, onMessage }: { step: MotionStepInput; parsed: MotionStep; scene: Scene; slotLabel: (slot: string) => string; onMessage: (m: { tone: 'info' | 'warning' | 'error'; text: string } | null) => void }) {
  const { motion, selection } = useServices();
  const state = useObservable(motion);
  const uid = useId();
  const info = TEMPLATES[step.template];
  const params = resolvedParams(parsed);
  const index = state.steps.findIndex((s) => s.id === step.id);
  const earlier = state.steps.slice(0, index);
  const update = (patch: Partial<MotionStepInput>) => motion.updateStep(step.id, patch);
  const timing = { mode: 'sequence' as const, ref: null, at: null, delay: 0, ...step.timing };
  const useSelection = () => {
    const objects = scene.objects.filter((o) => selection.get().objectIds.includes(o.id));
    const problem = motion.setTargets(step.id, objects);
    onMessage(problem ? { tone: 'error', text: problem } : null);
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-zinc-800 bg-zinc-900/60 p-2" role="group" aria-label={`Step ${index + 1}: ${info.label}`} data-testid="motion-inspector">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-tpl`} className={label}>
            Motion
          </label>
          <select id={`${uid}-tpl`} className={sel} value={step.template} onChange={(e) => update({ template: e.target.value as MotionStepInput['template'], params: {}, path: e.target.value === 'path' ? structuredClone(state.path) : null })} data-testid="motion-template">
            {[...CHARACTER_TEMPLATES, ...OBJECT_TEMPLATES].map((t) => (
              <option key={t} value={t}>
                {TEMPLATES[t].label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-0.5 text-xs text-zinc-300">
          <span className={label}>Targets</span>
          <span data-testid="motion-step-targets">{step.targets.map(slotLabel).join(', ')}</span>
        </div>
        <Button size="sm" onClick={useSelection} data-testid="motion-use-selection">
          Use canvas selection
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400">{info.description}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-mode`} className={label}>
            Starts
          </label>
          <select
            id={`${uid}-mode`}
            className={sel}
            value={timing.mode}
            onChange={(e) => update({ timing: { ...timing, mode: e.target.value as 'sequence', ref: e.target.value === 'with' || e.target.value === 'after' ? (earlier.at(-1)?.id ?? null) : null, at: e.target.value === 'at' ? (timing.at ?? 0) : null } })}
            data-testid="motion-timing-mode"
          >
            <option value="sequence">after the previous step</option>
            <option value="with" disabled={!earlier.length}>
              with a step
            </option>
            <option value="after" disabled={!earlier.length}>
              after a step
            </option>
            <option value="at">at a time</option>
          </select>
        </div>
        {(timing.mode === 'with' || timing.mode === 'after') && (
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${uid}-ref`} className={label}>
              Step
            </label>
            <select id={`${uid}-ref`} className={sel} value={timing.ref ?? ''} onChange={(e) => update({ timing: { ...timing, ref: e.target.value || null } })}>
              {earlier.map((s) => (
                <option key={s.id} value={s.id}>
                  {state.steps.indexOf(s) + 1}. {TEMPLATES[s.template].label}
                </option>
              ))}
            </select>
          </div>
        )}
        {timing.mode === 'at' && <NumberField id={`${uid}-at`} text="At (s)" value={timing.at} min={0} max={120} onChange={(v) => update({ timing: { ...timing, at: v ?? 0 } })} testId="motion-at" />}
        <NumberField id={`${uid}-delay`} text="Delay (s)" value={timing.delay || null} min={0} max={120} placeholder="0" onChange={(v) => update({ timing: { ...timing, delay: v ?? 0 } })} testId="motion-delay" />
        <NumberField id={`${uid}-dur`} text="Duration (s)" value={step.duration ?? null} min={0.05} max={120} placeholder={`auto (${info.defaultSeconds})`} onChange={(v) => update({ duration: v })} testId="motion-duration" />
        <NumberField id={`${uid}-rep`} text={info.cyclic ? 'Cycles' : 'Repetitions'} value={step.repeat ?? 1} step={1} min={1} max={64} onChange={(v) => update({ repeat: Math.max(1, Math.round(v ?? 1)) })} testId="motion-repeat" />
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-ease`} className={label}>
            Easing
          </label>
          <select id={`${uid}-ease`} className={sel} value={step.easing ?? ''} onChange={(e) => update({ easing: (e.target.value || null) as MotionStepInput['easing'] })} data-testid="motion-easing">
            <option value="">default ({info.defaultEasing})</option>
            {EasingSchema.options.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>
        {step.targets.length > 1 && <NumberField id={`${uid}-stagger`} text="Stagger (s)" value={step.stagger ?? 0} min={0} max={10} onChange={(v) => update({ stagger: v ?? 0 })} />}
      </div>
      {info.params.length > 0 && (
        <div className="flex flex-wrap items-end gap-2" role="group" aria-label="Parameters">
          {info.params.map((def) => (
            <ParamInput key={def.key} id={`${uid}-${def.key}`} def={def} value={params[def.key] ?? null} explicit={step.params?.[def.key] !== undefined} onChange={(v) => motion.setStepParam(step.id, def.key, v)} />
          ))}
        </div>
      )}
      {step.template === 'path' && <PathEditor path={step.path ?? state.path} onChange={(path) => (update({ path }), motion.update({ path }))} />}
      <div className="flex flex-wrap items-end gap-2 rounded border border-dashed border-zinc-700 p-2" role="group" aria-label="Seeded variation (optional)">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-var`} className={label}>
            Seeded variation (optional): {Math.round((step.variation ?? 0) * 100)}%
          </label>
          <input id={`${uid}-var`} type="range" min={0} max={1} step={0.05} value={step.variation ?? 0} onChange={(e) => update({ variation: Number(e.target.value) })} data-testid="motion-variation" />
        </div>
        <NumberField id={`${uid}-seed`} text="Seed" value={step.seed ?? null} step={1} min={0} placeholder={`plan (${state.seed})`} onChange={(v) => update({ seed: v === null ? null : Math.max(0, Math.round(v)) })} />
        <p className="text-[11px] text-zinc-400">0% = none. The same seed always gives the same values.</p>
      </div>
    </div>
  );
}

function ParamInput({ id, def, value, explicit, onChange }: { id: string; def: ParamDef; value: number | string | boolean | null; explicit: boolean; onChange: (v: number | string | boolean | null) => void }) {
  if (def.kind === 'boolean') {
    return (
      <label htmlFor={id} className="flex items-center gap-1 text-xs text-zinc-300" title={def.help}>
        <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} data-testid={`motion-param-${def.key}`} />
        {def.label}
      </label>
    );
  }
  if (def.kind === 'enum') {
    return (
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className={label} title={def.help}>
          {def.label}
        </label>
        <select id={id} className={sel} value={String(value)} onChange={(e) => onChange(e.target.value)} data-testid={`motion-param-${def.key}`}>
          {def.options.map((o) => (
            <option key={o} value={o}>
              {def.labels?.[o] ?? o}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <NumberField
      id={id}
      text={`${def.label}${def.unit ? ` (${def.unit})` : ''}`}
      value={explicit || def.default !== null ? (value as number | null) : null}
      step={def.step}
      min={def.min}
      max={def.max}
      placeholder={def.default === null ? 'auto' : undefined}
      onChange={(v) => onChange(v)}
      testId={`motion-param-${def.key}`}
    />
  );
}

/** Path points as "x, y" lines (scene px; offsets from the start when relative). */
function PathEditor(props: { path: MotionPath; onChange: (p: MotionPath) => void }) {
  return <PathEditorFields key={JSON.stringify(props.path.points)} {...props} />;
}

function PathEditorFields({ path, onChange }: { path: MotionPath; onChange: (p: MotionPath) => void }) {
  const uid = useId();
  const [text, setText] = useState(path.points.map(([x, y]) => `${x}, ${y}`).join('\n'));
  const [problem, setProblem] = useState<string | null>(null);
  const commit = () => {
    const points = text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => l.split(/[\s,;]+/).map(Number) as [number, number]);
    if (points.some((p) => p.length !== 2 || !p.every(Number.isFinite))) return setProblem('Each line needs two numbers: x, y.');
    if (points.length < 2) return setProblem('A path needs at least two points.');
    if (path.kind === 'bezier' && (points.length - 1) % 3 !== 0) return setProblem('A Bézier path has 3n+1 points: start, then two controls and an end per segment.');
    setProblem(null);
    onChange({ ...path, points });
  };
  return (
    <div className="flex flex-col gap-1 rounded border border-pink-900/60 p-2" role="group" aria-label="Path" data-testid="motion-path">
      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-300">
        <label htmlFor={`${uid}-kind`} className={label}>
          Path
        </label>
        <select id={`${uid}-kind`} className={sel} value={path.kind} onChange={(e) => onChange({ ...path, kind: e.target.value as MotionPath['kind'] })} data-testid="motion-path-kind">
          <option value="linear">Line / polyline</option>
          <option value="bezier">Smooth Bézier</option>
        </select>
        {(['relative', 'orient', 'reverse'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1">
            <input type="checkbox" checked={path[k]} onChange={(e) => onChange({ ...path, [k]: e.target.checked })} />
            {k === 'relative' ? 'Relative to start' : k === 'orient' ? 'Face along path' : 'Reverse'}
          </label>
        ))}
      </div>
      <label htmlFor={`${uid}-pts`} className={label}>
        Points (x, y per line; anchor positions in scene px{path.relative ? ', relative to where the object is' : ''})
      </label>
      <textarea id={`${uid}-pts`} rows={4} className={`${sel} font-mono text-xs`} value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} data-testid="motion-path-points" />
      {problem && (
        <p role="alert" className="text-xs text-red-400">
          {problem}
        </p>
      )}
    </div>
  );
}

function PlanOptions() {
  const { motion } = useServices();
  const state = useObservable(motion);
  const uid = useId();
  if (!state.steps.length) return null;
  return (
    <div className="flex flex-wrap items-end gap-2" role="group" aria-label="Plan options">
      <NumberField id={`${uid}-total`} text="Plan length (s, optional)" value={state.totalSeconds} min={0.1} max={120} placeholder="none" onChange={(v) => motion.update({ totalSeconds: v })} testId="motion-total" />
      <NumberField id={`${uid}-seed`} text="Plan seed" value={state.seed} step={1} min={0} onChange={(v) => motion.update({ seed: Math.max(0, Math.round(v ?? 1)) })} />
      <p className="text-[11px] text-zinc-400">With a plan length, flexible steps without a duration share the remaining time.</p>
    </div>
  );
}

function ApplyBar({ active, preview, onApply }: { active: Animation | null; preview: MotionPreview; onApply: () => void }) {
  const { motion } = useServices();
  const state = useObservable(motion);
  const uid = useId();
  if (!state.steps.length) return null;
  const r = preview.result;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-policy`} className={label}>
            Existing keyframes
          </label>
          <select id={`${uid}-policy`} className={sel} value={state.policy} onChange={(e) => motion.update({ policy: e.target.value as ConflictPolicy })} data-testid="motion-policy">
            {(Object.keys(CONFLICT_POLICIES) as ConflictPolicy[]).map((p) => (
              <option key={p} value={p} disabled={p !== 'new-animation' && !active}>
                {CONFLICT_POLICIES[p].label}
              </option>
            ))}
          </select>
        </div>
        {state.policy === 'new-animation' ? (
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${uid}-name`} className={label}>
              New animation name
            </label>
            <input id={`${uid}-name`} className={`${sel} w-40`} value={state.newAnimationName} maxLength={60} placeholder="Animation N" onChange={(e) => motion.update({ newAnimationName: e.target.value })} />
          </div>
        ) : (
          <label className="flex items-center gap-1 text-xs text-zinc-300">
            <input type="checkbox" checked={state.allowExtend} onChange={(e) => motion.update({ allowExtend: e.target.checked })} data-testid="motion-extend" />
            Lengthen the animation if the plan needs it
          </label>
        )}
        <Button size="sm" variant={state.preview ? 'danger' : 'secondary'} onClick={() => motion.update({ preview: !state.preview })} aria-pressed={state.preview} data-testid="motion-preview">
          {state.preview ? 'Previewing — stop' : 'Preview'}
        </Button>
        <Button size="sm" variant="primary" onClick={onApply} disabled={!r} data-testid="motion-apply">
          Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={() => motion.clear()}>
          Clear plan
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400">{CONFLICT_POLICIES[state.policy].description}</p>
      <div aria-live="polite" data-testid="motion-status" data-ok={r ? 'true' : 'false'}>
        {r && (
          <p className="text-xs text-emerald-300">
            {r.summary.replace(/^Added to/, 'Will add to').replace(/^Created/, 'Will create')} {preview.ms > 0 && <span className="text-zinc-400">· compiled in {preview.ms.toFixed(1)} ms</span>}
          </p>
        )}
        {preview.error && (
          <div className="text-xs text-red-300" role="alert">
            <p>{preview.error.message}</p>
            {preview.error.issues.length > 1 && (
              <ul className="ml-4 list-disc">
                {preview.error.issues.slice(0, 8).map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {(r ?? null)?.compiled.warnings.map((w) => (
          <p key={w.message} className="text-xs text-amber-300">
            ⚠ {w.message}
          </p>
        ))}
        {r && r.conflicts.length > 0 && (
          <ul className="text-xs text-red-300" data-testid="motion-conflicts" aria-label="Keyframes that will be replaced">
            {r.conflicts.map((c) => (
              <li key={`${c.objectId}/${c.channel}`}>Replaces: {describeConflict(c)}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Recipes({ project, scene, repo, onMessage }: { project: Project; scene: Scene; repo: ReturnType<typeof useServices>['recipes']; onMessage: (m: { tone: 'info' | 'warning' | 'error'; text: string } | null) => void }) {
  const { motion, selection } = useServices();
  const state = useObservable(motion);
  const [list, setList] = useState<MotionRecipe[]>([]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'portable' | 'project'>('portable');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const uid = useId();
  const refresh = useCallback(
    () =>
      repo.list().then(
        (r) => {
          setList(r.recipes);
          if (r.corrupted.length) onMessage({ tone: 'warning', text: `${r.corrupted.length} stored recipe(s) could not be read and were skipped.` });
        },
        (e: unknown) => onMessage({ tone: 'error', text: e instanceof Error ? e.message : 'Recipes could not be loaded.' }),
      ),
    [repo, onMessage],
  );
  useEffect(() => {
    let live = true;
    repo.list().then(
      (r) => live && setList(r.recipes),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [repo]);
  const guard = async (work: () => Promise<void>, done?: string) => {
    try {
      await work();
      await refresh();
      if (done) onMessage({ tone: 'info', text: done });
    } catch (e) {
      onMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) });
    }
  };
  const save = () =>
    guard(async () => {
      const draft = motion.draft();
      if (!draft) throw new Error('Add steps before saving a recipe.');
      await repo.save(createRecipe(draft.plan, name.trim() || 'Motion recipe', { scope, projectId: project.id, binding: draft.binding }));
      setName('');
    }, scope === 'portable' ? 'Saved as a portable recipe (no object ids; retarget it to any compatible objects).' : 'Saved as a project-local recipe (remembers the objects it was built for).');
  const load = (r: MotionRecipe) => {
    const binding = suggestBinding(r, scene, project.id, selection.get().objectIds);
    try {
      retargetRecipe(r, binding, scene, project.characters);
      onMessage({ tone: 'info', text: `Loaded “${r.name}”. Check its targets, preview, then apply.` });
    } catch (e) {
      onMessage({ tone: 'warning', text: `Loaded “${r.name}”: ${e instanceof Error ? e.message : String(e)} Choose its targets below.` });
    }
    motion.loadRecipe(r, binding);
  };
  const onImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    await guard(async () => {
      const recipes = parseRecipeFile(await f.text(), new Set(list.map((r) => r.id)));
      await repo.save(...recipes);
    }, `Imported ${f.name}.`);
  };
  return (
    <details className="rounded-md border border-zinc-800 p-2" data-testid="motion-recipes">
      <summary className="cursor-pointer text-[11px] font-semibold tracking-wide text-zinc-300 uppercase">Recipes ({list.length}) · saved in this browser</summary>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-name`} className={label}>
            Recipe name
          </label>
          <input id={`${uid}-name`} className={`${sel} w-44`} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-testid="motion-recipe-name" />
        </div>
        <div className="flex flex-col gap-0.5">
          <label htmlFor={`${uid}-scope`} className={label}>
            Kind
          </label>
          <select id={`${uid}-scope`} className={sel} value={scope} onChange={(e) => setScope(e.target.value as 'portable' | 'project')}>
            <option value="portable">Portable (no object ids)</option>
            <option value="project">Project-local (remembers objects)</option>
          </select>
        </div>
        <Button size="sm" onClick={() => void save()} disabled={!state.steps.length} data-testid="motion-recipe-save">
          Save plan as recipe
        </Button>
        <Button size="sm" onClick={() => file.current?.click()}>
          Import…
        </Button>
        <input ref={file} type="file" accept=".json,application/json" className="sr-only" aria-label="Import recipes" onChange={(e) => void onImport(e)} data-testid="motion-recipe-import" />
        <Button size="sm" onClick={() => downloadText(recipeFileName(list), serializeRecipes(list))} disabled={!list.length}>
          Export all
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="mt-2 text-xs text-zinc-400">No recipes yet. Build a plan and save it to reuse it on other characters, objects and projects.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1" aria-label="Saved recipes">
          {list.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-1 rounded bg-zinc-900/60 px-2 py-1 text-xs" data-testid="motion-recipe">
              {renaming?.id === r.id ? (
                <form
                  className="flex items-center gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void guard(async () => {
                      await repo.save(renameRecipe(r, renaming.name));
                      setRenaming(null);
                    });
                  }}
                >
                  <label htmlFor={`${uid}-rn`} className="sr-only">
                    New name
                  </label>
                  <input id={`${uid}-rn`} className={`${sel} w-40`} value={renaming.name} maxLength={60} onChange={(e) => setRenaming({ id: r.id, name: e.target.value })} />
                  <Button size="sm" type="submit">
                    Save
                  </Button>
                </form>
              ) : (
                <span className="flex-1 truncate text-zinc-200">
                  {r.name}{' '}
                  <span className="text-zinc-400">
                    · {r.plan.steps.length} step{r.plan.steps.length === 1 ? '' : 's'} · {r.plan.slots.map((s) => `${s.label} (${s.kind})`).join(', ')} · {r.scope === 'project' ? (r.projectId === project.id ? 'project-local' : 'project-local (other project)') : 'portable'}
                  </span>
                </span>
              )}
              <Button size="sm" onClick={() => load(r)} aria-label={`Load recipe ${r.name}`}>
                Load
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: r.id, name: r.name })} aria-label={`Rename recipe ${r.name}`}>
                Rename
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void guard(() => repo.save(duplicateRecipe(r)))} aria-label={`Duplicate recipe ${r.name}`}>
                Duplicate
              </Button>
              {r.scope === 'project' && (
                <Button size="sm" variant="ghost" onClick={() => void guard(() => repo.save(portableCopy(r)))} aria-label={`Make a portable copy of ${r.name}`}>
                  Portable copy
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => downloadText(recipeFileName([r]), serializeRecipes([r]))} aria-label={`Export recipe ${r.name}`}>
                Export
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void guard(() => repo.delete(r.id))} aria-label={`Delete recipe ${r.name}`}>
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
